/**
 * EmployeesPage — internal tooling visible only to TrainLoop employees.
 *
 * Access is guarded in App.tsx via the is_employee flag from /api/auth/me.
 * Direct URL access by non-employees redirects to /.
 *
 * Primary tool: the team impersonation picker. Selecting a team enters
 * read-only impersonation (re-scopes every request via X-Impersonate-Team).
 * Per-team config (sidebar, pipelines, billing) has its own pages under
 * /employees/teams/:teamId (components/internal/team-config), reached from
 * each row's Configure link, so the list stays scannable as the team count
 * and the number of settings grow. Opening that area ends impersonation:
 * staff configure a team as themselves (TeamConfigLayout).
 *
 * Below the teams: the proxy catalog (CatalogSection, TRA-1507), the base
 * models every team can call, managed here with no deploy.
 */

import { useMemo, useState } from 'react'
import { ChevronRight, Eye, Plus, Search, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { ImpersonatableTeam } from '@/api/generated/model'
import { CatalogSection } from '@/components/internal/CatalogSection'
import { teamConfigPath } from '@/components/internal/team-config/paths'
import { TeamCreateDialog } from '@/components/internal/TeamCreateDialog'
import { NAV_ENTRIES } from '@/lib/nav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { useTeam } from '@/contexts/TeamContext'
import { useInternalTeams } from '@/hooks/useInternalTeams'

/**
 * "Auto-join: acme.com · Hidden: Models, Review · Lands on Evals" — or
 * null for an invite-only team with the default sidebar.
 */
function teamSummary(team: ImpersonatableTeam): string | null {
  const parts: string[] = []
  if (team.email_domains.length > 0) {
    parts.push(`Auto-join: ${team.email_domains.join(', ')}`)
  }
  if (team.hidden_nav_items.length > 0) {
    const labels = team.hidden_nav_items.map(
      to => NAV_ENTRIES.find(e => e.to === to)?.label ?? to
    )
    parts.push(`Hidden: ${labels.join(', ')}`)
  }
  if (team.landing_page !== '/') {
    const label =
      NAV_ENTRIES.find(e => e.to === team.landing_page)?.label ?? team.landing_page
    parts.push(`Lands on ${label}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

interface TeamRowProps {
  team: ImpersonatableTeam
  /** This team is the one currently being impersonated. */
  isActive: boolean
  /** The viewer belongs to this team, so impersonation is not offered. */
  isOwn: boolean
  onImpersonate: (teamId: string) => void
}

/**
 * One list row: identity on the left, the Impersonate action (the page's
 * primary tool) in the middle, and the Configure link to the team's
 * configuration pages on the right. The last grid column is fixed so
 * every row's Configure link sits on one vertical line whether or not
 * the Impersonate button renders.
 */
function TeamRow({
  team,
  isActive,
  isOwn,
  onImpersonate,
}: TeamRowProps): React.JSX.Element {
  const summary = teamSummary(team)
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-foreground">
            {team.name}
          </p>
          {isActive && <Badge>Impersonating</Badge>}
          {isOwn && (
            <Badge
              variant="secondary"
              title="You belong to this team. Use the team switcher instead"
            >
              Member
            </Badge>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {team.slug}
          {summary && <span> · {summary}</span>}
        </p>
      </div>
      {!isActive && !isOwn && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onImpersonate(team.id)
          }}
        >
          <Eye className="h-3.5 w-3.5" />
          Impersonate
        </Button>
      )}
      <Button variant="outline" size="sm" className="col-start-3" asChild>
        <Link to={teamConfigPath(team.id)} aria-label={`Configure ${team.name}`}>
          Configure
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </li>
  )
}

export default function EmployeesPage(): React.JSX.Element {
  const { impersonatingTeamId, startImpersonation, teams: ownTeams } = useTeam()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const teamsQuery = useInternalTeams()

  // Own teams stay in the list so their sidebar is configurable (TRA-927),
  // but Impersonate is not offered for them: impersonating a team you belong
  // to is a no-op read-only downgrade (the backend rejects it with 400) and
  // pollutes the cross-team audit trail — use the team switcher instead.
  // Restricted demo teams are excluded from the ownership set: /me appends
  // them for EVERY user regardless of membership (TRA-832), so treating
  // them as "own" would falsely label staff as members and hide
  // impersonation on teams they don't belong to.
  const ownTeamIds = useMemo(
    () => new Set(ownTeams.filter(t => !t.is_restricted).map(t => t.id)),
    [ownTeams]
  )

  const filtered = useMemo(() => {
    const teams = teamsQuery.data?.teams ?? []
    const q = search.trim().toLowerCase()
    if (!q) return teams
    return teams.filter(
      t =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q)
    )
  }, [teamsQuery.data, search])

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="TrainLoop Employees"
        description="Internal tooling. Only TrainLoop staff can see this page."
      />

      <section className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-serif text-lg font-medium text-foreground">
              Teams
            </h2>
            <p className="text-sm text-muted-foreground">
              Impersonate a team to view its workspace read-only (audited), or
              create a company team with auto-join email domains. Configure
              opens the staff settings for a team and ends impersonation.
            </p>
          </div>
          <Button
            onClick={() => {
              setCreateOpen(true)
            }}
          >
            <Plus className="h-4 w-4" />
            Create team
          </Button>
        </div>

        <TeamCreateDialog open={createOpen} onOpenChange={setCreateOpen} />

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value)
            }}
            placeholder="Search teams by name, slug, or id…"
            className="pl-9"
          />
        </div>

        {teamsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading teams…</p>
        ) : teamsQuery.isError ? (
          <EmptyState
            icon={Shield}
            title="Couldn’t load teams"
            description="The team list failed to load. Try refreshing."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={search.trim() ? 'No matching teams' : 'No teams'}
            description={
              search.trim()
                ? 'No teams match your search.'
                : 'No teams exist yet. Create one above.'
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {filtered.map(team => (
              <TeamRow
                key={team.id}
                team={team}
                isActive={team.id === impersonatingTeamId}
                isOwn={ownTeamIds.has(team.id)}
                onImpersonate={startImpersonation}
              />
            ))}
          </ul>
        )}
      </section>

      <CatalogSection />
    </PageContainer>
  )
}
